/**
 * useIsMobile Hook - UX2
 *
 * Detects if the user is on a mobile device based on screen width.
 * Uses a responsive breakpoint of 768px (tablet and below).
 *
 * @returns {boolean} True if screen width <= 768px
 *
 * @example
 * ```tsx
 * function Component() {
 *   const isMobile = useIsMobile()
 *
 *   return (
 *     <div>
 *       {isMobile ? 'Mobile View' : 'Desktop View'}
 *     </div>
 *   )
 * }
 * ```
 *
 * Performance Notes:
 * - Uses matchMedia API for efficient media query detection
 * - Automatically updates when window is resized
 * - Cleans up event listeners on unmount
 */

import { useEffect, useState } from 'react'

const MOBILE_BREAKPOINT = 768 // px

export function useIsMobile(): boolean {
  // Default to false (desktop) for SSR
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // Check if window is available (not SSR)
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }

    // Create media query for mobile detection
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)

    // Set initial value
    setIsMobile(mediaQuery.matches)

    // Listen for changes
    const handler = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches)
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handler)
      return () => mediaQuery.removeListener(handler)
    }
  }, [])

  return isMobile
}

/**
 * Helper function to detect mobile device synchronously
 * Use this for one-time checks without React hooks
 *
 * @returns {boolean} True if current viewport is mobile
 *
 * @example
 * ```tsx
 * if (checkIsMobile()) {
 *   // Mobile-specific logic
 * }
 * ```
 */
export function checkIsMobile(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }

  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
}
